import { ToolContainer } from "@/components/ToolLayout";
import { BackupBar } from "@/components/BackupBar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from "docx";
import { downloadBlob } from "@/components/ToolLayout";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { addDays, parseLocalDate, toIsoLocal, useStored, useTrip } from "./tripStore";
import { PDFDocument, rgb } from "pdf-lib";
import { TextKit } from "../business/pdfText";
import { decodeTrip, encodeTrip } from "./shareCodec";
import { useEffect } from "react";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { useShareGate } from "@/hooks/useShareGate";
import { copyText } from "@/lib/copyText";

interface DayPlan {
  id: number;
  title: string;
  notes: string;
}

const TOOL_INFO_CONTENT = {
  what: `Builds a day-by-day plan for a trip, with a title and notes for each day (and real dates once you set the first day), saved in your browser, so you're not trying to remember or improvise a plan once you've already arrived.`,
  how: `Add a day for each day of your trip, give it a title and notes, set the first day to get dates, and export the itinerary as text, a Word file, a PDF or a calendar (.ics) file, or copy a link that carries the whole plan inside it.`,
  tip: `Leave at least one day intentionally loose rather than scheduling every single day tightly — travel plans often shift once you're actually there, and an open day absorbs that naturally instead of derailing the rest of the schedule.`,
};

const DEFAULT_DAYS: DayPlan[] = [
  { id: 1, title: "Arrival & Check-in", notes: "Flight lands, settle into hotel, explore nearby area" },
  { id: 2, title: "Sightseeing Day", notes: "" },
];

export default function TravelItineraryPlanner() {
  const [trip, setTrip] = useTrip();
  const [days, setDays, resetDays] = useStored<DayPlan[]>("fdp-itinerary-days-v1", DEFAULT_DAYS);
  const tripName = trip.name;
  const start = parseLocalDate(trip.start);
  const nextId = days.reduce((m, d) => Math.max(m, d.id), 0) + 1;
  const dateOf = (i: number) => (start ? addDays(start, i) : null);
  const dateText = (i: number) => dateOf(i)?.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) ?? "";

  const addDay = () => setDays([...days, { id: nextId, title: `Day ${days.length + 1}`, notes: "" }]);
  const removeDay = (id: number) => setDays(days.filter(d => d.id !== id));
  const moveDay = (i: number, d: number) => { const j = i + d; if (j < 0 || j >= days.length) return; const c = days.slice(); [c[i], c[j]] = [c[j], c[i]]; setDays(c); };
  const updateDay = (id: number, field: keyof DayPlan, value: string) => {
    setDays(days.map(d => (d.id === id ? { ...d, [field]: value } : d)));
  };

  const { logUsageNow } = useLogUsageOnAction();
  const { gateShare } = useShareGate();

  const exportText = async () => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    let text = `${tripName}\n${"=".repeat(tripName.length)}\n\n`;
    days.forEach((d, i) => {
      text += `Day ${i + 1}${dateText(i) ? ` (${dateText(i)})` : ""}: ${d.title}\n`;
      if (d.notes) text += `${d.notes}\n`;
      text += "\n";
    });
    void copyText(text, "Itinerary copied to clipboard");
  };

  const exportWord = async () => {
    const children: Paragraph[] = [
      new Paragraph({
        children: [new TextRun({ text: tripName, bold: true, size: 32 })],
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 },
      }),
    ];
    days.forEach((d, i) => {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: `Day ${i + 1}${dateText(i) ? ` (${dateText(i)})` : ""}: ${d.title}`, bold: true, size: 28 })],
          spacing: { before: 200, after: 100 },
        })
      );
      if (d.notes) {
        children.push(new Paragraph({ children: [new TextRun({ text: d.notes, size: 24 })], spacing: { after: 150 } }));
      }
    });
    const doc = new Document({ sections: [{ children }] });
    const blob = await Packer.toBlob(doc);
    downloadBlob(blob, `${tripName.replace(/\s+/g, "-").toLowerCase() || "itinerary"}.docx`);
    toast.success("Itinerary downloaded!");
  };

  const exportPdf = async () => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    try {
      const lines = days.map((d, i) => ({ head: `Day ${i + 1}${dateText(i) ? ` — ${dateText(i)}` : ""}`, title: d.title, notes: d.notes }));
      const pdf = await PDFDocument.create();
      const kit = await TextKit.create(pdf, [tripName, ...lines.flatMap(l => [l.head, l.title, l.notes])].join("\n"));
      const PW = 612, PH = 792, M = 54, W = PW - 2 * M;
      let page = pdf.addPage([PW, PH]);
      let y = PH - M;
      const need = (h: number) => { if (y - h < M) { page = pdf.addPage([PW, PH]); y = PH - M; } };
      kit.draw(page, tripName, { x: M, y: y - 22, size: 22, bold: true });
      y -= 40;
      if (start) { kit.draw(page, `${dateText(0)} – ${dateText(days.length - 1)}`, { x: M, y: y - 10, size: 11, color: rgb(0.4, 0.4, 0.4) }); y -= 24; }
      for (const l of lines) {
        const notes = l.notes ? kit.wrap(l.notes, 11, W) : [];
        need(18 + 16 + Math.min(notes.length, 2) * 15 + 10);
        y -= 12;
        kit.draw(page, l.head, { x: M, y: y - 10, size: 10, color: rgb(0.35, 0.35, 0.35) }); y -= 16;
        for (const t of kit.wrap(l.title || "(untitled)", 14, W, true)) { need(18); kit.draw(page, t, { x: M, y: y - 12, size: 14, bold: true }); y -= 18; }
        for (const n of notes) { need(15); kit.draw(page, n, { x: M, y: y - 11, size: 11 }); y -= 15; }
        need(10); page.drawLine({ start: { x: M, y: y - 4 }, end: { x: PW - M, y: y - 4 }, thickness: 0.5, color: rgb(0.85, 0.85, 0.85) }); y -= 8;
      }
      await kit.finish();
      downloadBlob(new Blob([(await pdf.save()) as BlobPart], { type: "application/pdf" }), `${tripName.replace(/\s+/g, "-").toLowerCase() || "itinerary"}.pdf`);
      const note = kit.note();
      toast.success(note ? `Itinerary PDF downloaded. ${note}` : "Itinerary PDF downloaded");
    } catch (e) { console.error(e); toast.error("Couldn't build the PDF"); }
  };

  const copyShareLink = () => {
    const code = encodeTrip({ n: tripName, s: trip.start, d: days.map(d => ({ t: d.title, o: d.notes })) });
    const url = `${window.location.origin}${window.location.pathname}#plan=${code}`;
    if (url.length > 7000) { toast.error("This itinerary is too long for a link — use the Word or PDF download to share it"); return; }
    // The plan itself travels inside this link, so it is a result leaving the page: same gate as any share.
    return gateShare(() => navigator.clipboard.writeText(url).then(() => { toast.success("Link copied — the whole plan is inside the link itself, nothing is uploaded"); return true; }, () => { toast.error("Couldn't copy the link"); return false; }));
  };

  // Opening a shared link offers to load that plan; nothing is replaced until the person agrees.
  useEffect(() => {
    const m = /^#plan=([A-Za-z0-9_-]+)$/.exec(window.location.hash);
    if (!m) return;
    const t = decodeTrip(m[1]);
    history.replaceState(null, "", window.location.pathname + window.location.search);
    if (!t) { toast.error("That shared itinerary link is damaged"); return; }
    if (window.confirm(`Load the shared itinerary "${t.n}" (${t.d.length} days)? It replaces the itinerary saved in this browser.`)) {
      setTrip({ ...trip, name: t.n, start: t.s });
      setDays(t.d.map((x, i) => ({ id: i + 1, title: x.t, notes: x.o })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportIcs = async () => {
    if (!start) { toast.error("Set a start date first so each day has a date"); return; }
    const allowed = await logUsageNow();
    if (!allowed) return;
    const esc = (t: string) => t.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
    const ymd = (d: Date) => toIsoLocal(d).replace(/-/g, "");
    const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");
    const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//FreeDailyPro//Itinerary//EN"];
    days.forEach((d, i) => {
      const day = dateOf(i)!;
      lines.push("BEGIN:VEVENT", `UID:fdp-${d.id}-${ymd(day)}@freedailypro.com`, `DTSTAMP:${stamp}`, `DTSTART;VALUE=DATE:${ymd(day)}`, `DTEND;VALUE=DATE:${ymd(addDays(day, 1))}`, `SUMMARY:${esc(`${tripName}: ${d.title}`)}`);
      if (d.notes) lines.push(`DESCRIPTION:${esc(d.notes)}`);
      lines.push("END:VEVENT");
    });
    lines.push("END:VCALENDAR");
    downloadBlob(new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" }), `${tripName.replace(/\s+/g, "-").toLowerCase() || "itinerary"}.ics`);
    toast.success("Calendar file downloaded");
  };

  return (
    <ToolContainer>
      <div className="grid sm:grid-cols-2 gap-4 mb-6 max-w-2xl">
        <div>
          <Label htmlFor="trip-name" className="mb-2 block">Trip Name</Label>
          <Input id="trip-name" value={tripName} onChange={e => setTrip({ ...trip, name: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="trip-start" className="mb-2 block">First day (optional)</Label>
          <Input id="trip-start" type="date" value={trip.start} onChange={e => setTrip({ ...trip, start: e.target.value })} />
        </div>
      </div>

      <div className="space-y-4">
        {days.map((day, i) => (
          <Card key={day.id} className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm font-semibold text-muted-foreground w-16 shrink-0">
                Day {i + 1}
              </span>
              {dateOf(i) && <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">{dateText(i)}</span>}
              <Input
                value={day.title}
                onChange={e => updateDay(day.id, "title", e.target.value)}
                placeholder="Day title (e.g. Museum & Old Town)"
                className="flex-1"
              />
              <Button size="icon" variant="ghost" aria-label={`Move day ${i + 1} up`} disabled={i === 0} onClick={() => moveDay(i, -1)}>↑</Button>
              <Button size="icon" variant="ghost" aria-label={`Move day ${i + 1} down`} disabled={i === days.length - 1} onClick={() => moveDay(i, 1)}>↓</Button>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Remove day ${i + 1}`}
                onClick={() => removeDay(day.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea
              value={day.notes}
              onChange={e => updateDay(day.id, "notes", e.target.value)}
              placeholder="Plans, bookings, notes for this day..."
              className="min-h-[80px]"
            />
          </Card>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-3">Kept for this visit only (erased when you close the tab). Nothing is sent to a server.</p>
      <div className="flex flex-wrap gap-3 mt-4">
        <Button variant="outline" onClick={addDay}>
          <Plus className="h-4 w-4 mr-2" /> Add Day
        </Button>
        <Button onClick={exportText} variant="outline">Copy Full Itinerary</Button>
        <Button onClick={exportWord} className="gap-2"><Download className="h-4 w-4" /> Download as Word</Button>
        <Button onClick={exportPdf} variant="outline">Download as PDF</Button>
        <Button onClick={exportIcs} variant="outline">Add to calendar (.ics)</Button>
        <Button onClick={copyShareLink} variant="outline">Copy share link</Button>
        <Button onClick={() => { if (confirm("Clear the whole itinerary?")) resetDays(); }} variant="ghost">Clear</Button>
      </div>
          <BackupBar tool="travel-itinerary-planner" prefixes={["fdp-trip-", "fdp-itinerary-"]} session />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
